import { Tooltip } from "antd";

/**
 * Displays text with a tooltip if it exceeds the specified maximum length.
 * The tooltip shows the full text when hovering over truncated text.
 *
 * @param text - The text to display
 * @param maxLength - Maximum character length before showing tooltip (default: 10)
 * @returns React node with tooltip if text exceeds maxLength, otherwise plain text
 */
export const TextWithTooltip = (text: string, maxLength: number = 10): React.ReactNode => {
  if (!text) return null;

  const isLong = text.length > maxLength;
  const displayText = isLong ? text.substring(0, maxLength) + "..." : text;

  return isLong ? (
    <Tooltip title={text}>
      <span className="truncate">{displayText}</span>
    </Tooltip>
  ) : (
    <span className="truncate">{displayText}</span>
  );
};

export const TextWithTooltipWrap = (text: string, maxLength: number = 10): React.ReactNode => {
  if (!text) return null;

  const isLong = text.length > maxLength;
  const displayText = isLong ? text.substring(0, maxLength) + "..." : text;

  return isLong ? (
    <Tooltip title={text}>
      <span className="!whitespace-pre-wrap">{displayText}</span>
    </Tooltip>
  ) : (
    <span className="!whitespace-pre-wrap">{displayText}</span>
  );
};

/**
 * Displays text with a tooltip if it exceeds the specified maximum length.
 * Uses CSS truncate (ellipsis) instead of manually cutting text.
 * The tooltip shows the full text when hovering over truncated text.
 *
 * @param text - The text to display
 * @param maxLength - Maximum character length before showing tooltip (default: 10)
 * @returns React node with tooltip if text exceeds maxLength, otherwise plain text with truncate
 */
export const TextWithTooltipAutoTruncate = (text: string, maxLength: number = 10): React.ReactNode => {
  if (!text) return null;

  const isLong = text.length > maxLength;

  return isLong ? (
    <Tooltip title={text} placement="topLeft">
      <span className="truncate">{text}</span>
    </Tooltip>
  ) : (
    <span className="truncate">{text}</span>
  );
};
